import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { listSupportDriverNotes, createSupportDriverNote } from "../../../../../api/adminSupportApi";

function safeStr(x) {
  return x == null ? "" : String(x).trim();
}
function fmtDT(v) {
  if (!v) return "—";
  try {
    const d = new Date(String(v));
    if (!Number.isNaN(d.getTime())) return d.toLocaleString("en-IN");
  } catch (_) {}
  return String(v);
}

function PaginationFooter({ page, totalPages, size, onSize, onPrev, onNext, totalElements }) {
  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500">
          Page <span className="font-semibold text-slate-700">{page + 1}</span> of{" "}
          <span className="font-semibold text-slate-700">{Math.max(1, totalPages)}</span>
          {Number.isFinite(Number(totalElements)) ? (
            <>
              {" "}
              <span className="text-slate-300">•</span>{" "}
              <span className="font-semibold text-slate-700">{Number(totalElements).toLocaleString("en-IN")}</span>{" "}
              total
            </>
          ) : null}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold text-slate-500">View</div>
            <div className="relative">
              <select
                value={size}
                onChange={(e) => onSize(Number(e.target.value))}
                className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50 outline-none"
              >
                {[10, 20, 30, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">▼</span>
            </div>
          </div>

          <div className="text-sm text-slate-700">
            Page <span className="font-semibold">{page + 1}</span> of{" "}
            <span className="font-semibold">{Math.max(1, totalPages)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onPrev}
              disabled={page <= 0}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              onClick={onNext}
              disabled={page + 1 >= totalPages}
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SupportDriverNotesTab() {
  const ctx = useOutletContext() || {};
  const driverId = ctx.driverId;

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(30);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const rows = data?.content || [];
  const totalPages = Number(data?.totalPages ?? 1);
  const totalElements = Number(data?.totalElements ?? 0);

  const key = useMemo(() => `${driverId}|${page}|${size}`, [driverId, page, size]);

  useEffect(() => {
    if (!driverId) return;
    let alive = true;
    setLoading(true);
    setErr("");

    listSupportDriverNotes(driverId, { page, size })
      .then((res) => alive && setData(res))
      .catch((e) => alive && setErr(e?.message || "Failed to load notes"))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [key, driverId]);

  async function onCreate() {
    const n = safeStr(text);
    if (!n || !driverId) return;
    setSaving(true);
    setErr("");
    try {
      await createSupportDriverNote(driverId, n);
      setText("");
      // refresh list (first page shows newest in most setups; your backend can be either way)
      setPage(0);
      const fresh = await listSupportDriverNotes(driverId, { page: 0, size });
      setData(fresh);
    } catch (e) {
      setErr(e?.message || "Failed to create note");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Notes</div>
        <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-center">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a support note for this driver…"
            className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
          />
          <button
            onClick={onCreate}
            disabled={saving || !safeStr(text)}
            className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-900 bg-slate-900 px-5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            Add note
          </button>
        </div>
      </div>

      {err ? (
        <div className="border-b border-slate-200 px-5 py-3 text-[12px] font-semibold text-rose-600">{err}</div>
      ) : null}

      <div className="divide-y divide-slate-200">
        {loading ? (
          <div className="px-6 py-10 text-sm font-semibold text-slate-600">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="text-sm font-semibold text-slate-900">No notes</div>
            <div className="mt-1 text-[12px] text-slate-500">Add a note to capture support context.</div>
          </div>
        ) : (
          rows.map((n) => (
            <div key={n.id} className="px-6 py-4">
              <div className="text-[13px] text-slate-900 whitespace-pre-wrap">{n.note || "—"}</div>
              <div className="mt-2 text-[12px] text-slate-500 tabular-nums">
                {fmtDT(n.createdAt)} {n.createdByAdminId != null ? <span> • by admin #{n.createdByAdminId}</span> : null}
              </div>
            </div>
          ))
        )}
      </div>

      <PaginationFooter
        page={page}
        totalPages={totalPages}
        size={size}
        onSize={(n) => {
          setSize(n);
          setPage(0);
        }}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        totalElements={totalElements}
      />
    </div>
  );
}
